import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { formatLocationName } from '~/utils';
import { withBranchGroups, type BranchCoded } from '~/utils/locationBranch';

type SelectableLocation = BranchCoded &
  NonNullable<Parameters<typeof formatLocationName>[0]> & { id: string };

type LocationSelectProps<T extends SelectableLocation> = Omit<
  ComponentProps<typeof SearchableSelect>,
  'options' | 'error'
> & {
  locations: T[];
  /** Adds a leading option with value `all` under this label. */
  allLabel?: string;
  /** Defaults to name and street address. */
  getLabel?: (location: T) => string;
  /** Pass with `error` to show load status in the placeholder and disable the select. */
  isLoading?: boolean;
  error?: unknown;
};

export function LocationSelect<T extends SelectableLocation>({
  locations,
  allLabel,
  getLabel = formatLocationName,
  isLoading,
  error,
  placeholder,
  disabled,
  ...props
}: LocationSelectProps<T>) {
  const { t } = useTranslation('general');
  const tracksLoad = isLoading !== undefined || error !== undefined;
  let status = placeholder;
  if (tracksLoad) {
    if (isLoading) status = t('location_select.loading');
    else if (error) status = t('location_select.failed');
    else if (locations.length === 0) status = t('location_select.empty');
    else status ??= t('location_select.placeholder');
  }
  const options = withBranchGroups(locations, (location) => ({
    value: location.id,
    label: getLabel(location),
  }));
  return (
    <SearchableSelect
      {...props}
      placeholder={status}
      disabled={
        disabled ||
        (tracksLoad && (!!isLoading || !!error || locations.length === 0))
      }
      options={
        allLabel ? [{ value: 'all', label: allLabel }, ...options] : options
      }
    />
  );
}
